import { useEffect, useMemo, useState } from 'react';
import { Fuel, ShoppingCart, Loader2, Minus, Plus, MapPin } from 'lucide-react';
import ModalShell from '../../components/ui/ModalShell';
import { AKCENT, OK, POZOR, CHYBA, Tlacitko, tint, cinnostMista } from './ui';

/**
 * Přidat zastávku — benzinka nebo obchod s potravinami (2026-10-29).
 *
 * Místa jsou z OpenStreetMap v okruhu kolem bistra, seřazená podle toho,
 * o kolik prodlouží trasu (vzdušný odhad, hned). Po přidání server místo
 * vloží do trasy tam, kde nejméně zdrží, a přepočítá odhady přes silnice.
 * Doba pobytu jde upravit po minutách.
 */
export default function MistaSheet({ davkaId, poloha = null, onPridano, onClose, isDark }) {
    const [mista, setMista] = useState(null);
    const [err, setErr] = useState(null);
    const [filtr, setFiltr] = useState('vse');
    const [vybrane, setVybrane] = useState(null);
    const [pobyt, setPobyt] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const q = new URLSearchParams();
        if (davkaId) q.set('davka', davkaId);
        if (poloha?.lat) { q.set('lat', poloha.lat); q.set('lng', poloha.lng); }
        fetch(`/api/rozvoz/mista?${q}`, { credentials: 'same-origin' })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(d => setMista(d.mista ?? []))
            .catch(e => setErr(e.message));
        // Poloha jen při otevření — seznam se za jízdy nepřeskupuje pod prstem.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [davkaId]);

    const seznam = useMemo(() => (mista ?? [])
        .filter(m => filtr === 'vse' || m.druh === filtr)
        .slice(0, 25), [mista, filtr]);

    const minut = (m) => pobyt[m.id] ?? m.pobyt_min;
    const zmenit = (m, o) => setPobyt(p => ({ ...p, [m.id]: Math.max(1, Math.min(60, minut(m) + o)) }));
    const vybraneMisto = (mista ?? []).find(m => m.id === vybrane);

    const pridat = async () => {
        if (!vybraneMisto) return;
        setBusy(true);
        setErr(null);
        try {
            const r = await fetch(`/api/rozvoz/davky/${davkaId}/mista`, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ misto_id: vybraneMisto.id, pobyt_min: minut(vybraneMisto) }),
            });
            const json = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
            onPridano?.(json);
        } catch (e) {
            setErr(e.message);
            setBusy(false);
        }
    };

    return (
        <ModalShell open onClose={onClose} zavritLze={!busy} isDark={isDark} nadpis="Přidat zastávku" ikona={MapPin} barva={AKCENT}
            paticka={
                <Tlacitko plne barva={AKCENT} onClick={pridat} disabled={!vybraneMisto || busy} style={{ width: '100%' }}>
                    {busy && <Loader2 size={18} className="animate-spin" />}
                    {vybraneMisto
                        ? `Přidat · ${vybraneMisto.nazev} · +${vybraneMisto.zajizdka_min + minut(vybraneMisto)} min`
                        : 'Vyber místo'}
                </Tlacitko>
            }>
            <div style={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    {[['vse', 'Vše'], ['benzinka', 'Benzinky'], ['obchod', 'Obchody']].map(([k, t]) => (
                        <button key={k} type="button" onClick={() => setFiltr(k)} style={{
                            height: 38, padding: '0 14px', borderRadius: 999, font: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                            border: '1px solid var(--border-default)',
                            background: filtr === k ? 'var(--text-primary)' : 'var(--surface-elevated)',
                            color: filtr === k ? 'var(--surface-base)' : 'var(--text-secondary)',
                        }}>{t}</button>
                    ))}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)' }}>
                    Podle zajížďky — kolik minut jízdy navíc. K tomu se připočte čas na místě.
                </div>

                {!mista && !err && <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><Loader2 className="animate-spin" style={{ color: AKCENT }} /></div>}
                {err && <div style={{ fontSize: 13.5, fontWeight: 700, color: CHYBA }}>{err}</div>}
                {mista && seznam.length === 0 && (
                    <div style={{ fontSize: 14, color: 'var(--text-secondary)', padding: '8px 2px' }}>V okolí není žádné místo tohoto druhu.</div>
                )}

                {seznam.map(m => {
                    const Ik = m.druh === 'benzinka' ? Fuel : ShoppingCart;
                    const ted = vybrane === m.id;
                    const barva = m.zajizdka_min <= 3 ? OK : m.zajizdka_min <= 8 ? POZOR : CHYBA;
                    return (
                        <div key={m.id} role="button" tabIndex={0} onClick={() => setVybrane(m.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setVybrane(m.id); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 14, cursor: 'pointer',
                                background: 'var(--surface-elevated)',
                                border: ted ? `2px solid ${AKCENT}` : '2px solid transparent',
                                opacity: m.v_trase ? 0.55 : 1,
                            }}>
                            <span style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tint(AKCENT, 0.14), color: AKCENT }}>
                                <Ik size={20} />
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 15.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.nazev}</div>
                                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <b style={{ color: barva }}>+{m.zajizdka_min} min</b>
                                    {' · '}{(m.vzdalenost_m / 1000).toFixed(1).replace('.', ',')} km od bistra
                                    {m.adresa ? ` · ${m.adresa}` : ''}
                                    {m.v_trase ? ' · už v trase' : ''}
                                </div>
                            </div>
                            {ted && (
                                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                    <button type="button" onClick={() => zmenit(m, -1)} aria-label="Méně minut" style={krok}><Minus size={16} /></button>
                                    <div style={{ textAlign: 'center', minWidth: 34 }}>
                                        <div style={{ fontSize: 17, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>{minut(m)}</div>
                                        <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)' }}>min</div>
                                    </div>
                                    <button type="button" onClick={() => zmenit(m, 1)} aria-label="Víc minut" style={krok}><Plus size={16} /></button>
                                </div>
                            )}
                            {!ted && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>{cinnostMista(m.druh)} {m.pobyt_min} min</span>}
                        </div>
                    );
                })}
            </div>
        </ModalShell>
    );
}

const krok = {
    width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border-default)', cursor: 'pointer',
    background: 'var(--surface-base)', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
